import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";
import type { LogEntry } from "../../API/Models";
import { formatRelativeTimeFr } from "../../Helper/dates";
import { activityMetaFor } from "../../Helper/activityMeta";
import { badgeForLog, buildLogLink } from "../../Helper/activityAction";
import { isAutomated, isDashboardNoise } from "../../Realtime/activityStream";
import { PanelCard } from "./dashboardShared";
import { UserAvatar } from "../UserAvatar";
import { useUserDirectory } from "../Account/useUserDirectory";

const sameUser = (a?: string | null, b?: string) => (a ?? "").trim().toLowerCase() === (b ?? "").trim().toLowerCase();

/** Le journal porte l'email de l'acteur depuis peu (userEmail) : c'est la SEULE clé fiable,
 *  deux comptes pouvant partager le même nom d'affichage. Les entrées antérieures n'ont que
 *  le nom ; on retombe alors dessus, faute de mieux. */
type FeedEntry = LogEntry & { userEmail?: string | null };
const actorKey = (entry: FeedEntry): string => {
	const email = (entry.userEmail ?? "").trim();
	return email.length > 0 ? email : (entry.user ?? "");
};
const isMine = (entry: FeedEntry, currentUser: string, currentUserEmail?: string): boolean => {
	const email = (entry.userEmail ?? "").trim();
	if (email && (currentUserEmail ?? "").trim()) return sameUser(email, currentUserEmail);
	return sameUser(entry.user, currentUser);
};

/** Chip d'action coloré (Création / Modification / Préversion / Production / Dépublication…). */
const ActionBadge: React.FC<{ entry: LogEntry }> = ({ entry }) => {
	const badge = badgeForLog(entry);
	return (
		<span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${badge.chip}`}>
			{badge.label}
		</span>
	);
};

/** A machine event (FX rates, auto-publish, SYSTEM bulk jobs) : one calm muted line, but still
 *  badged, clickable when it references a content, and timestamped. One row = one entry (no fold). */
const AutomatedRow: React.FC<{ entry: LogEntry }> = ({ entry }) => {
	const meta = activityMetaFor(entry.type);
	const link = buildLogLink(entry);
	const message = entry.message || meta?.label || "Mise à jour automatique";
	return (
		<li className="flex items-center gap-2.5 px-5 py-2">
			<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-50 text-slate-400">
				{meta?.icon ?? <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />}
			</span>
			<ActionBadge entry={entry} />
			{link ? (
				<Link to={link} className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-500 hover:text-sky-700 hover:underline" title={entry.message ?? ""}>
					{message}
				</Link>
			) : (
				<span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-500" title={entry.message ?? ""}>{message}</span>
			)}
			<span className="ml-auto shrink-0 text-[11px] text-slate-400">{formatRelativeTimeFr(entry.createdAt)}</span>
		</li>
	);
};

/** A human action : full emphasis: action badge + bold message on top, actor · time below.
 *  The message links to the page (/page/{lang}/{slug}) or the record (/content/{schemaKey}/{id})
 *  whenever the entry's tags reference a content; the avatar/name link to the actor's profile. */
const HumanRow: React.FC<{
	entry: FeedEntry;
	currentUser: string;
	currentUserEmail?: string;
	resolveUserId: (n?: string | null) => string | null;
	resolveUserPicture?: (n?: string | null) => string | null;
}> = ({ entry, currentUser, currentUserEmail, resolveUserId, resolveUserPicture }) => {
	const link = buildLogLink(entry);
	const mine = isMine(entry, currentUser, currentUserEmail);
	const actor = mine ? "Vous" : entry.user && entry.user.trim() ? entry.user : "SYSTEM";
	// Resolve the actor id once, EMAIL FIRST (stable key : display names collide) : used both
	// for the profile link and to colour the avatar per account.
	const actorId = resolveUserId(actorKey(entry));
	// Profile link: my own row → /account; another known actor → /account/{id}.
	const profileHref = mine ? "/account" : actorId ? `/account/${actorId}` : null;

	const avatar = (
		<UserAvatar
			name={entry.user ?? ""}
			id={actorId ?? undefined}
			pictureUrl={resolveUserPicture?.(actorKey(entry))}
			sizeClass="h-7 w-7"
			className="mt-0.5"
			ringClass={mine ? "ring-2 ring-sky-400" : ""}
		/>
	);

	return (
		<li className={`px-5 py-2.5 ${mine ? "border-l-2 border-sky-400" : ""}`}>
			<div className="flex items-start gap-2.5">
				{profileHref ? (
					<Link to={profileHref} aria-label={`Profil de ${actor}`} className="transition hover:opacity-90">
						{avatar}
					</Link>
				) : (
					avatar
				)}
				<div className="min-w-0 flex-1">
					<div className="flex items-start gap-2">
						<ActionBadge entry={entry} />
						{link ? (
							<Link to={link} className="block min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 hover:text-sky-700 hover:underline">
								{entry.message || "Événement"} <FiExternalLink className="ml-0.5 inline h-3 w-3 text-slate-400" />
							</Link>
						) : (
							<p className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800" title={entry.message ?? ""}>
								{entry.message || "Événement"}
							</p>
						)}
					</div>
					<div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
						{profileHref ? (
							<Link to={profileHref} className="font-medium text-slate-600 hover:text-sky-700 hover:underline">{actor}</Link>
						) : (
							<span className="font-medium text-slate-600">{actor}</span>
						)}
						<span>·</span>
						<span>{formatRelativeTimeFr(entry.createdAt)}</span>
					</div>
				</div>
			</div>
		</li>
	);
};

/** Renders the activity rows : ONE ROW PER ENTRY, no ×N fold (human actions emphasised, machine
 *  events demoted). Shared by the dashboard panel and the paginated "see all" modal so both feeds
 *  look identical. */
export const ChangesFeedList: React.FC<{
	entries: LogEntry[];
	currentUser: string;
	currentUserEmail?: string;
	resolveUserId: (n?: string | null) => string | null;
	resolveUserPicture?: (n?: string | null) => string | null;
}> = ({ entries, currentUser, currentUserEmail, resolveUserId, resolveUserPicture }) => (
	<ul className="divide-y divide-slate-100">
		{entries.map((entry, index) =>
			isAutomated(entry) ? (
				<AutomatedRow key={entry.id ?? `cf-${index}`} entry={entry} />
			) : (
				<HumanRow key={entry.id ?? `cf-${index}`} entry={entry} currentUser={currentUser} currentUserEmail={currentUserEmail} resolveUserId={resolveUserId} resolveUserPicture={resolveUserPicture} />
			)
		)}
	</ul>
);

export interface ChangesFeedProps {
	logs: LogEntry[];
	loading: boolean;
	currentUser: string;
	/** Email du compte connecté : la clé stable pour « Vous » et le filtre « me »
	 *  (deux comptes peuvent partager le même nom d'affichage). */
	currentUserEmail?: string;
	/** "me" → only the current admin's actions; "all" → everyone. */
	scope: "me" | "all";
	title: string;
	subtitle?: string;
	limit?: number;
	connected?: boolean;
	bodyClassName?: string;
	/** Extra classes on the card (the grid item) : e.g. column-span overrides at a breakpoint. */
	className?: string;
	/** When set, a "Voir tout" button appears in the header and calls this to open the paginated modal. */
	onShowAll?: () => void;
}

/** Recent-changes feed (dashboard). One row per entry : no ×N grouping. The 30-min ForexAPI beat and
 *  the exchange-rate mirror's technical writes are filtered out here (pure noise); the real rate
 *  changes (`forex-rates`) stay visible. The « Voir tout » modal shows everything, unfiltered. */
export const ChangesFeed: React.FC<ChangesFeedProps> = ({ logs, loading, currentUser, currentUserEmail, scope, title, subtitle, limit = 8, connected, bodyClassName, className, onShowAll }) => {
	const { resolveId, resolvePicture } = useUserDirectory();
	const entries = useMemo(() => {
		const relevant = logs.filter((l) => !isDashboardNoise(l));
		const base = scope === "me" ? relevant.filter((l) => isMine(l, currentUser, currentUserEmail)) : relevant;
		return base.slice(0, limit);
	}, [logs, scope, currentUser, currentUserEmail, limit]);

	const emptyText = scope === "me" ? "Aucune modification de votre part." : "Aucune activité récente.";

	return (
		<PanelCard
			title={title}
			subtitle={subtitle}
			className={className}
			bodyClassName={bodyClassName ?? "max-h-[320px] overflow-auto"}
			action={
				connected !== undefined || onShowAll ? (
					<div className="flex items-center gap-2">
						{connected !== undefined && (
							<span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-500">
								<span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
								{connected ? "Direct" : "Hors ligne"}
							</span>
						)}
						{onShowAll && (
							<button
								type="button"
								onClick={onShowAll}
								className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50"
							>
								Voir tout <FiExternalLink className="h-3 w-3" />
							</button>
						)}
					</div>
				) : undefined
			}
		>
			{loading ? (
				<ul className="divide-y divide-slate-100">
					{Array.from({ length: 5 }).map((_, i) => (
						<li key={`cf-skel-${i}`} className="flex items-center gap-3 px-5 py-3">
							<span className="h-7 w-7 rounded-full bg-slate-100" />
							<div className="h-3 w-2/3 rounded bg-slate-100" />
						</li>
					))}
				</ul>
			) : entries.length === 0 ? (
				<div className="px-5 py-10 text-center text-sm text-slate-500">{emptyText}</div>
			) : (
				<ChangesFeedList entries={entries} currentUser={currentUser} currentUserEmail={currentUserEmail} resolveUserId={resolveId} resolveUserPicture={resolvePicture} />
			)}
		</PanelCard>
	);
};

export default ChangesFeed;
